// ABOUTME: React component for family or child heart success rate analytics card
// ABOUTME: Displays heart-shaped fill indicator, 12-week average, summary counter, and individual child status

import React, { memo, useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Avatar,
  CircularProgress
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { getCurrentAccessToken } from '../services/api';
import { statusTint } from '../theme/tokens';

interface ChildBehaviorCounts {
  starCount: number;
  triangleCount: number;
  squareCount: number;
  makeItRightCount: number;
  netSquares: number;
}

interface ChildHeartStatus {
  childId: number;
  childName: string;
  initial: string;
  isOnTrack: boolean;
  status: string;
  behaviorCounts: ChildBehaviorCounts;
}

interface HeartSuccessRateData {
  familyId: number;
  totalChildren: number;
  childrenOnTrack: number;
  successPercentage: number;
  childStatuses: ChildHeartStatus[];
  date: string;
  familyTimezone: string;
}

interface HeartSuccessRateCardProps {
  familyId: number;
  childId?: number;
  familyData: HeartSuccessRateData | null;
  twelveWeekAverage?: number;
}

// Styled components for heart shape
const HeartContainer = styled(Box)(() => ({
  position: 'relative',
  width: 80,
  height: 80,
  margin: '0 auto 16px auto',
}));

const HeartShape = styled(Box)<{ fillPercentage: number; averagePercentage?: number }>(({ theme, fillPercentage, averagePercentage }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .heart-outline': {
    position: 'absolute',
    fontSize: '64px',
    color: theme.palette.grey[300],
    zIndex: 1,
  },
  '& .heart-fill': {
    position: 'absolute',
    fontSize: '64px',
    background: `linear-gradient(to top, ${theme.palette.behaviorHeart} 0%, ${theme.palette.behaviorHeart} ${fillPercentage}%, transparent ${fillPercentage}%, transparent 100%)`,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
    zIndex: 2,
  },
  '& .heart-average-line': {
    position: 'absolute',
    fontSize: '64px',
    background: averagePercentage ? `linear-gradient(to top, transparent 0%, transparent ${averagePercentage - 2}%, ${theme.palette.info.main} ${averagePercentage - 2}%, ${theme.palette.info.main} ${averagePercentage + 2}%, transparent ${averagePercentage + 2}%, transparent 100%)` : 'none',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
    zIndex: 3,
  }
}));

const SummaryCounter = styled(Box)<{ allOnTrack: boolean; totalChildren: number; childrenOnTrack: number }>(({ theme, allOnTrack, totalChildren, childrenOnTrack }) => {
  // Determine color based on percentage
  let borderColor, backgroundColor;

  if (totalChildren === 0) {
    borderColor = theme.palette.grey[500];
    backgroundColor = theme.palette.grey[50];
  } else if (allOnTrack) {
    // All on track = green
    borderColor = theme.palette.success.main;
    backgroundColor = statusTint.successBg;
  } else if (childrenOnTrack === 0) {
    // None on track = red
    borderColor = theme.palette.error.main;
    backgroundColor = statusTint.errorBg;
  } else {
    // Some on track = yellow/orange
    borderColor = theme.palette.warning.main;
    backgroundColor = statusTint.warningBg;
  }

  return {
    padding: '12px 16px',
    borderRadius: `${Number(theme.shape.borderRadius) * (2 / 3)}px`, // = 8px
    backgroundColor,
    border: `2px solid ${borderColor}`,
    textAlign: 'center',
    marginBottom: '16px',
    fontWeight: 400,
  };
});

const ChildStatusChip = styled(Chip)<{ childStatus: string }>(({ theme, childStatus }) => {
  let borderColor, backgroundColor;

  switch (childStatus) {
    case 'On Track':
      borderColor = theme.palette.success.main;
      backgroundColor = theme.palette.common.white;
      break;
    case 'Square':
      borderColor = theme.palette.error.main;
      backgroundColor = theme.palette.common.white;
      break;
    case 'Not on Track':
      borderColor = theme.palette.warning.main;
      backgroundColor = theme.palette.common.white;
      break;
    default:
      borderColor = theme.palette.grey[400];
      backgroundColor = theme.palette.common.white;
      break;
  }

  return {
    backgroundColor,
    border: `2px solid ${borderColor}`,
    color: theme.palette.text.primary,
    fontWeight: 600,
    '& .MuiChip-label': {
      padding: '4px 8px',
    },
  };
});

const HeartSuccessRateCard: React.FC<HeartSuccessRateCardProps> = memo(({
  familyId,
  childId,
  familyData: initialFamilyData,
  twelveWeekAverage: initialTwelveWeekAverage
}) => {
  const theme = useTheme();
  const [familyData, setFamilyData] = useState<HeartSuccessRateData | null>(initialFamilyData);
  const [twelveWeekAverage, setTwelveWeekAverage] = useState<number | undefined>(initialTwelveWeekAverage);
  const [loading, setLoading] = useState(!initialFamilyData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialFamilyData !== null) {
      setFamilyData(initialFamilyData);
      setLoading(false);
      return;
    }

    const loadHeartSuccessData = async () => {
      if (!familyId) return;

      setLoading(true);
      setError(null);

      try {
        const authToken = getCurrentAccessToken();

        // Construct URLs with optional childId parameter
        const baseUrl = `/api/heart-success-rate/family/${familyId}`;
        const averageUrl = `/api/heart-success-rate/family/${familyId}/twelve-week-average`;
        const dataUrl = childId ? `${baseUrl}?childId=${childId}` : baseUrl;
        const avgUrl = childId ? `${averageUrl}?childId=${childId}` : averageUrl;

        // Load current heart success rate data
        const heartResponse = await fetch(dataUrl, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (heartResponse.ok) {
          const heartData = await heartResponse.json();
          setFamilyData(heartData);
        } else {
          console.error('Heart success rate API failed:', heartResponse.status, await heartResponse.text());
          setError('Failed to load heart success rate data');
        }

        // Load twelve-week average
        const averageResponse = await fetch(avgUrl, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (averageResponse.ok) {
          const averageData = await averageResponse.json();
          setTwelveWeekAverage(averageData.twelveWeekAveragePercentage);
        } else {
          console.error('Twelve week average API failed:', averageResponse.status, await averageResponse.text());
        }
      } catch (error) {
        console.error('Failed to load heart success rate data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadHeartSuccessData();
  }, [familyId, childId, initialFamilyData]);

  if (loading) {
    return (
      <Card elevation={2} sx={{ p: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Today&apos;s Success Rate
          </Typography>
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !familyData) {
    return (
      <Card elevation={2} sx={{ p: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Today&apos;s Success Rate
          </Typography>
          <Typography color="text.secondary">
            {error || 'No data available'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (familyData.totalChildren === 0) {
    return (
      <Card elevation={2} sx={{ p: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Today&apos;s Success Rate
          </Typography>
          <Typography color="text.secondary">
            No children in your family yet.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // In child context, show only that specific child's data
  const isChildContext = !!childId;
  const currentChild = isChildContext ? familyData.childStatuses.find(c => c.childId === childId) : null;

  if (isChildContext && !currentChild) {
    return (
      <Card elevation={2} sx={{ p: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom align="center">
            Today&apos;s Success Rate
          </Typography>
          <Typography color="text.secondary" align="center">
            Child data not found.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // For child context, calculate stats based on single child
  const childrenOnTrack = isChildContext ? (currentChild?.isOnTrack ? 1 : 0) : familyData.childrenOnTrack;
  const totalChildren = isChildContext ? 1 : familyData.totalChildren;
  const successPercentage = isChildContext ? (currentChild?.isOnTrack ? 100 : 0) : familyData.successPercentage;
  const allChildrenOnTrack = childrenOnTrack === totalChildren;

  return (
    <Card elevation={2} sx={{ p: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom align="center">
          {isChildContext ? `${currentChild?.childName}'s Success Rate` : 'Today\'s Success Rate'}
        </Typography>

        {/* Summary Counter - Above Heart */}
        <SummaryCounter
          allOnTrack={allChildrenOnTrack}
          totalChildren={totalChildren}
          childrenOnTrack={childrenOnTrack}
          data-testid="summary-counter"
        >
          <Typography variant="body2">
            {isChildContext && currentChild ? (
              currentChild.isOnTrack ?
                <><strong>{currentChild.childName}</strong> is <strong>On Track</strong> to earn a heart for today.</> :
                <><strong>{currentChild.childName}</strong> is <strong>Not On Track</strong> to earn a heart today.</>
            ) : (
              <><strong>{childrenOnTrack}</strong> of <strong>{totalChildren}</strong> Children are{' '}
              <strong>On Track</strong> to earn a heart for today.</>
            )}
          </Typography>
        </SummaryCounter>

        {/* Heart Shape Indicator */}
        <HeartContainer>
          <HeartShape
            fillPercentage={successPercentage}
            averagePercentage={twelveWeekAverage}
            data-testid="heart-fill-indicator"
          >
            <span className="heart-outline">♥</span>
            <span className="heart-fill">♥</span>
            {twelveWeekAverage && <span className="heart-average-line">♥</span>}
          </HeartShape>
        </HeartContainer>

        {/* 12-Week Average */}
        <Typography
          variant="body2"
          align="center"
          sx={{
            color: theme.palette.info.main,
            mb: 2,
            fontSize: '12px',
            fontStyle: 'italic'
          }}
          data-testid="twelve-week-average"
        >
          12-week average: {twelveWeekAverage !== undefined ? `${twelveWeekAverage}%` : '--'}
        </Typography>

        {/* Individual Child Status - Responsive Grid Layout */}
        {/* In child context, show only the current child; in family context, show all children */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: 2,
            justifyItems: 'center'
          }}
        >
          {(isChildContext ? (currentChild ? [currentChild] : []) : familyData.childStatuses).map((child) => (
            <Box
              key={child.childId}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                maxWidth: '120px'
              }}
            >
              {/* Child Avatar */}
              <Avatar
                sx={{
                  bgcolor: theme.palette.info.main,
                  color: theme.palette.info.contrastText,
                  width: 40,
                  height: 40,
                  // fontSize matches h6/body1, but Avatar has no Typography variant to swap to
                  fontSize: '16px',
                  fontWeight: 600
                }}
                data-testid={`child-initial-${child.childId}`}
              >
                {child.initial}
              </Avatar>

              {/* Child Name */}
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, textAlign: 'center', fontSize: '12px' }}
                data-testid={`child-name-${child.childId}`}
              >
                {child.childName}
              </Typography>

              {/* Status Chip */}
              <ChildStatusChip
                label={child.status}
                size="small"
                childStatus={child.status}
                data-testid={`child-status-${child.childId}`}
                sx={{ fontSize: '10px' }}
              />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
});

HeartSuccessRateCard.displayName = 'HeartSuccessRateCard';

export default HeartSuccessRateCard;